import { parse, tokenizer } from 'acorn';
import { collectionKinds } from './drift';
import { listStartingRows } from './gate';
import { humanize } from './readable';

/**
 * An edit that changed the STARTING rows of a list the owner already manages — which changes
 * nothing anybody sees.
 *
 * Seen live on 2026-09-29. A café owner asked chat to "remove the Preview tasting event and the
 * Loyalty card launch" from the site's opening-week offers. The offers are an `S.list`: its
 * starting rows are written into the collection ONCE, when the app is first published
 * (`seedOwnerContent`, only for an app created in that publish), and from then on the rows are
 * the owner's, edited in Manage → Live. The edit took the two offers out of the code, the live
 * rows were untouched, and chat answered "Done — your live page is updated". Every visitor still
 * saw both offers until the owner found them in Manage and deleted them by hand.
 *
 * The honest fix is to SAY it, not to write the new rows over the live ones: those rows may be
 * the owner's own edits since the first publish, and a chat sentence about two offers must not
 * be able to overwrite them.
 *
 * Compared as ROWS, not as text: the same rows re-quoted, re-spaced or with their keys in another
 * order are the same rows, and an ordinary edit ("make the header green") must say nothing extra.
 * A list the edit ADDED is left out — `addedLists` says that one, because what is true of it is
 * different: it has no rows on the page at all — and so is one it dropped, which `subjectDrift`
 * already reports as stranded.
 */
export function changedListRows(before: string, after: string): string[] {
  const kindsBefore = collectionKinds(before);
  const kindsAfter = collectionKinds(after);
  const rowsBefore = listStartingRows(before);
  const rowsAfter = listStartingRows(after);

  const changed: string[] = [];
  for (const [collection, kinds] of kindsBefore) {
    if (!kinds.has('list') || !kindsAfter.get(collection)?.has('list')) continue;
    /*
     * Rows that cannot be read count as none. Rows the model moved out of reach — into a call,
     * say — were never going to be written either, and the live ones still did not move, so
     * saying so is true in every case where this errs.
     */
    if (canonical(rowsBefore[collection] ?? []) !== canonical(rowsAfter[collection] ?? [])) {
      changed.push(collection);
    }
  }
  return changed;
}

/**
 * The S.list lists an edit ADDED — names the app did not use before, for any kind of storage.
 *
 * The first fix left these out "because they have no live rows to contradict" (review,
 * 2026-09-29). They have no live rows at ALL. Starting rows reach a collection only through
 * `seedOwnerContent`, which runs for an app created in that same publish (`if (createdAppId)` in
 * saddleHost.ts — the first publish, or a deleted app published again), and the web's publish
 * never seeds. An edit republishes an app that exists, so an added list paints the model's rows
 * from its first-paint cache (`list` in runtime/js.ts) and then the server's empty list — while
 * chat said "Done". Nothing is seeded here to make up for it: a chat edit writing rows into a live
 * collection is the thing `changedListRows` refuses to do, for the same reason.
 *
 * Found whatever its rows, written or not, readable or not: it starts empty either way.
 *
 * Only a name ABSENT before. A name kept under another kind (`S.store('specials')` becoming
 * `S.list('specials')`) is a move, which `subjectDrift` reports as stranding what was saved; what
 * a moved name holds on the server is not known here, so "starts empty" is not said about it. One
 * gap left: a name an earlier edit dropped and this one brings back may still hold that list's old
 * rows, and then it does not start empty — Manage → Live, where the sentence sends the owner,
 * shows them.
 */
export function addedLists(before: string, after: string): string[] {
  const kindsBefore = collectionKinds(before);
  return [...collectionKinds(after)]
    .filter(([collection, kinds]) => kinds.has('list') && !kindsBefore.has(collection))
    .map(([collection]) => collection);
}

/**
 * The rows an edit WROTE for the photos that came with it, per `S.list` — what chat may offer,
 * in one tap, to add to the live list.
 *
 * F10 (deep-dive, 2026-09-30): the wedding's gallery is `S.list('photos')`. The owner attached
 * three photos in web chat and wrote "add these to our engagement gallery"; the photos uploaded,
 * the edit was charged and wrote three starting rows into the code, and the live collection
 * visitors see did not change — starting rows reach it only at the app's first publish.
 *
 * `changedListRows` refuses to write the edit's rows over the live ones, and that stands: they
 * may be the owner's own edits. This is narrower, and only ever ADDS: a row counts only if the
 * edit wrote it (no row like it was there before — compared without its code `id`, which the
 * server assigns its own of) and it names one of `photos`, the files attached to THIS message,
 * anywhere in its values (`row.src`, `row.image.src`). Only a collection that is an `S.list` and
 * nothing else — one a page also submits into is not the owner's to write.
 *
 * The rows come back without their `id`, ready for `addManagedRow`. Total: an unreadable source
 * reads as having no rows, as in `changedListRows`.
 */
export function photoRowsToAdd(
  before: string,
  after: string,
  photos: string[],
): { list: string; rows: Record<string, unknown>[] }[] {
  const names = new Set(photos.map((p) => p.trim()).filter(Boolean));
  if (!names.size) return [];
  const rowsOf = (source: string) => {
    try {
      return listStartingRows(source);
    } catch {
      return {};
    }
  };
  const rowsBefore = rowsOf(before);
  const rowsAfter = rowsOf(after);
  const withoutId = (row: Record<string, unknown>) => {
    const { id: _id, ...rest } = row;
    return rest;
  };
  const namesAPhoto = (value: unknown): boolean => {
    if (typeof value === 'string') return names.has(value.trim());
    if (Array.isArray(value)) return value.some(namesAPhoto);
    if (value && typeof value === 'object') return Object.values(value as Record<string, unknown>).some(namesAPhoto);
    return false;
  };

  const out: { list: string; rows: Record<string, unknown>[] }[] = [];
  for (const [list, kinds] of collectionKinds(after)) {
    if (kinds.size !== 1 || !kinds.has('list')) continue;
    const had = new Set((rowsBefore[list] ?? []).map((r) => canonical(withoutId(r))));
    const rows = (rowsAfter[list] ?? [])
      .map(withoutId)
      .filter((r) => Object.keys(r).length > 0 && !had.has(canonical(r)) && namesAPhoto(r));
    if (rows.length) out.push({ list, rows });
  }
  return out;
}

/** "your photos list", "your price list" — a list named as chat names it, for an offer's button. */
export function spokenList(collection: string): string {
  return named([collection]).lists;
}

/**
 * A value as one string, with object keys in a fixed order — so `{ id, title }` and
 * `{ title, id }` compare equal and a re-ordered ROW list does not.
 *
 * Total, deliberately. This runs after the edit is SAVED and before it is published, so a throw
 * would leave the owner's saved change off their page over a sentence about a list. `JSON.stringify`
 * throws on a BigInt, which is a literal acorn reads (`10n`).
 */
function canonical(value: unknown): string {
  if (typeof value === 'bigint') return `${value}n`;
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    if (value instanceof RegExp) return String(value);
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
  }
  return value === undefined ? 'undefined' : JSON.stringify(value);
}

/**
 * A collection's code name in the owner's words, mid-sentence: the name Manage → Live heads it
 * with (`humanize`), its first letter lower-cased unless the first word is an acronym. So
 * `openingWeekOffers` and `opening-week_offers` are both "opening week offers", `imageUrls` is
 * "image URLs" and `faqs` is "FAQs".
 *
 * It had a splitter of its own until review of the list-edits merge (2026-09-29): chat said
 * "your image url list" about the list Manage heads "Image URL", and the sentence sends the owner
 * to Manage to find it by that name.
 */
function spoken(name: string): string {
  const words = humanize(name);
  // A heading capitalises only a first word's first letter; a capital after it is an acronym's.
  return /^\S\S*?\p{Lu}/u.test(words) ? words : words.charAt(0).toLowerCase() + words.slice(1);
}

/**
 * Whether an edit changed NOTHING but S.list starting rows — everything outside those row
 * literals the same before and after, but for what never reaches the page (below).
 *
 * The café's reply after the first fix still opened "Done — your live page is updated." and only
 * then said the rows had not changed (review, 2026-09-29). The rows were the whole edit, so nothing
 * on the page had changed and the headline was false. What the headline may claim depends on
 * whether the edit changed anything ELSE, and this is how that is told.
 *
 * Each list's second argument is cut out whole, so re-spacing or re-quoting the rows reads as the
 * rows. Anything else that differs — a heading, a class, the code that renders the list — is
 * another change, and the page did change.
 *
 * Two things that change nothing on the page are not counted either (review of the list-edits
 * merge, 2026-09-29). The whitespace around the whole source: the edit's answer arrives trimmed
 * and a stored source may end in a newline, and that alone made the café's rows-only edit read
 * "Done — your page is updated, except…". And the script's comments and the spacing between its
 * tokens, which is how a model most often says what it did — "// Removed the two offers, as
 * asked." — so the script is compared token by token. A line break between tokens is kept, as
 * one can end a statement; HTML outside the scripts is still compared as written.
 */
export function sameApartFromListRows(before: string, after: string): boolean {
  return withoutListRows(before) === withoutListRows(after);
}

/**
 * The source, trimmed, with each script as its tokens and every `S.list(name, rows)` call's
 * `rows` as one marker.
 */
function withoutListRows(source: string): string {
  return source.trim().replace(/(<script\b[^>]*>)([\s\S]*?)(<\/script>)/gi, (whole, open: string, code: string, close: string) => {
    let program: unknown;
    try {
      program = parse(code, { ecmaVersion: 2022 });
    } catch {
      // Unreadable here means unreadable to `listStartingRows` too, which found no list in it.
      return whole;
    }
    const ranges: [number, number][] = [];
    walk(program, (n) => {
      if (!isListCall(n)) return;
      const rows = (n.arguments as Node[] | undefined)?.[1];
      if (rows) ranges.push([rows.start, rows.end]);
    });
    // Outermost first, and a range inside one already taken is dropped with it.
    ranges.sort((a, b) => a[0] - b[0]);
    const kept: [number, number][] = [];
    for (const r of ranges) if (!kept.length || r[0] >= kept[kept.length - 1][1]) kept.push(r);

    const out: string[] = [];
    let previousEnd = 0;
    let marked = -1;
    try {
      for (const token of tokenizer(code, { ecmaVersion: 2022 })) {
        // A comment is never a token, so it is in the gap — which says only whether a line broke.
        const gap = code.slice(previousEnd, token.start);
        previousEnd = token.end;
        const inRows = kept.findIndex(([start, end]) => token.start >= start && token.start < end);
        if (inRows !== -1) {
          if (marked !== inRows) out.push(' /* rows */');
          marked = inRows;
          continue;
        }
        // The spacing either side of the rows is theirs: inside a call's brackets, a line break
        // ends nothing.
        const rowsEdge = out[out.length - 1] === ' /* rows */';
        out.push(!rowsEdge && /[\n\r\u2028\u2029]/.test(gap) ? '\n' : ' ', code.slice(token.start, token.end));
      }
    } catch {
      return whole;
    }
    return `${open}${out.join('')}${close}`;
  });
}

interface Node {
  type: string;
  start: number;
  end: number;
  [key: string]: unknown;
}

function walk(node: unknown, visit: (n: Node) => void): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (const child of node) walk(child, visit);
    return;
  }
  const n = node as Node;
  if (typeof n.type === 'string') visit(n);
  for (const key of Object.keys(n)) {
    if (key === 'type' || key === 'loc' || key === 'range') continue;
    walk(n[key], visit);
  }
}

/** `S.list(…)` — the shape `listStartingRows` reads. */
function isListCall(n: Node): boolean {
  if (n.type !== 'CallExpression') return false;
  const callee = n.callee as { type?: string; object?: { type?: string; name?: string }; property?: { type?: string; name?: string } } | undefined;
  return callee?.type === 'MemberExpression'
    && callee.object?.type === 'Identifier' && callee.object.name === 'S'
    && callee.property?.type === 'Identifier' && callee.property.name === 'list';
}

/**
 * The lists, named in the owner's words: "offers list", "price list" (not "price list list"),
 * "offers and specials lists".
 */
function named(collections: string[]): { lists: string; one: boolean } {
  const names = collections.map(spoken);
  if (names.length === 1) {
    const [name] = names;
    return { lists: /list$/.test(name) ? name : `${name} list`, one: true };
  }
  return { lists: `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} lists`, one: false };
}

/**
 * Where an owner edits a list's live rows, in each client's own words.
 *
 * Every sentence below said "Manage → Live", which is the phone's; the web calls the same place
 * "Your data → Live", and an owner on the web was sent to a tab that does not exist there (F10,
 * deep-dive 2026-09-30). The client passes its own; absent is the phone's, which is what these
 * sentences have always said.
 */
export const PHONE_LIVE_PLACE = 'Manage → Live';
export const WEB_LIVE_PLACE = 'Your data → Live';

const liveRowsTail = (place: string) => `the rows already on your page did not change. Edit them in ${place}.`;

/**
 * What the owner is told about the lists `changedListRows` found — or undefined when there are
 * none. Said on both clients, through the edit's outcome (`runWebAppEdit`): after a headline
 * that makes no claim about the rows — the offline one — and on a publish that failed.
 */
export function liveRowsNotice(collections: string[], place: string = PHONE_LIVE_PLACE): string | undefined {
  if (!collections.length) return undefined;
  const { lists, one } = named(collections);
  return `Your ${lists} ${one ? 'is' : 'are'} live content: ${liveRowsTail(place)}`;
}

/**
 * The reply's HEADLINE for an edit that changed a live list's starting rows, in place of
 * "Done — your live page is updated." (`editReply`).
 *
 * `only`: the rows were all the edit changed (`sameApartFromListRows`), so nothing on the page
 * changed and "Done" would be false — the café's reply after the first fix. Otherwise the rest of
 * the edit is on the page, and the headline says so and names what is not.
 */
export function listRowsHeadline(collections: string[], only: boolean, place: string = PHONE_LIVE_PLACE): string {
  const { lists, one } = named(collections);
  return only
    ? `Saved — but your ${lists} did not change on your page: ${one ? 'it is' : 'they are'} live content. Edit ${one ? 'its' : 'their'} rows in ${place}.`
    : `Done — your page is updated, except your ${lists}, which ${one ? 'is' : 'are'} live content: ${liveRowsTail(place)}`;
}

/**
 * What the owner is told about the lists `addedLists` found — or undefined when there are none.
 * After the headline, which is true of the rest of the edit: the new list IS on the page, empty.
 */
export function newListsNotice(collections: string[], place: string = PHONE_LIVE_PLACE): string | undefined {
  if (!collections.length) return undefined;
  const { lists, one } = named(collections);
  return `Your new ${lists} ${one ? 'starts' : 'start'} empty on your page: add ${one ? 'its' : 'their'} rows in ${place}.`;
}
